import { useState, useEffect, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, CheckCircle, SkipForward, MessageSquare, AlertCircle, UserPlus } from 'lucide-react'
import { playbookApi, voipApi } from '../../api/endpoints'
import Button from '../ui/Button'
import Input, { Textarea } from '../ui/Input'

interface CallPulseModalProps {
  leadRef?: string | number | null
  callLogId?: number
  voipCallId?: number
  playbookId?: number
  resumeResponseSetId?: number
  callerNumber?: string | null
  onClose: () => void
  onLeadCreated?: (leadId: number, leadName: string) => void
}

interface PlaybookQuestion {
  id: number
  text: string
  question_type: string
  options: string[] | null
  required: boolean
  helper_text: string | null
  ai_tags: string[] | null
  sort_order: number
}

interface MatchedPlaybook {
  id: number
  name: string
  description: string | null
  version: number
  product: string
  questions: PlaybookQuestion[]
}

/** Build the response payload with correct field names for the backend. */
function _buildResponsePayload(
  responses: Record<number, string | string[]>,
  skipped: Set<number>,
): Array<{ question_id: number; answer_text: string; answer_json: string | null; skipped: boolean }> {
  const result = Object.entries(responses).map(([qId, val]) => ({
    question_id: parseInt(qId),
    answer_text: Array.isArray(val) ? val.join(', ') : val,
    answer_json: Array.isArray(val) ? JSON.stringify(val) : null,
    skipped: skipped.has(parseInt(qId)),
  }))
  skipped.forEach((qId) => {
    if (!result.find((r) => r.question_id === qId)) {
      result.push({ question_id: qId, answer_text: '', answer_json: null, skipped: true })
    }
  })
  return result
}

export default function CallPulseModal({ leadRef: initialLeadRef, callLogId, voipCallId, playbookId, resumeResponseSetId, callerNumber, onClose, onLeadCreated }: CallPulseModalProps) {
  const queryClient = useQueryClient()
  const [resolvedLeadRef, setResolvedLeadRef] = useState<string | number | null>(initialLeadRef ?? null)
  const [responseSetId, setResponseSetId] = useState<number | null>(resumeResponseSetId ?? null)
  const [responses, setResponses] = useState<Record<number, string | string[]>>({})
  const [skipped, setSkipped] = useState<Set<number>>(new Set())
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  // Update resolvedLeadRef when prop changes (e.g., lead created externally)
  useEffect(() => {
    if (initialLeadRef) setResolvedLeadRef(initialLeadRef)
  }, [initialLeadRef])

  const leadRef = resolvedLeadRef

  // When resuming a draft, fetch existing responses and populate state
  const { data: resumeData, isLoading: loadingResume } = useQuery({
    queryKey: ['resume-response-set', resumeResponseSetId],
    queryFn: () => playbookApi.getResponseSet(resumeResponseSetId!),
    enabled: !!resumeResponseSetId,
  })

  useEffect(() => {
    if (!resumeData?.data) return
    const rs = resumeData.data as any
    const rsList = rs.responses || []
    if (rsList.length === 0) return
    // Populate responses from existing answers
    const existingResponses: Record<number, string | string[]> = {}
    const existingSkipped = new Set<number>()
    for (const r of rsList) {
      if (r.skipped) {
        existingSkipped.add(r.question_id)
      } else if (r.answer_json) {
        // answer_json is already parsed by the API (array or string)
        const val = Array.isArray(r.answer_json) ? r.answer_json
          : typeof r.answer_json === 'string' ? (() => { try { return JSON.parse(r.answer_json) } catch { return r.answer_json } })()
          : r.answer_json
        existingResponses[r.question_id] = val
      } else if (r.answer_text) {
        existingResponses[r.question_id] = r.answer_text
      }
    }
    setResponses(existingResponses)
    setSkipped(existingSkipped)
  }, [resumeData])

  // Step 1a: Fetch specific playbook if playbookId is provided
  const { data: specificData, isLoading: loadingSpecific } = useQuery({
    queryKey: ['playbook', playbookId],
    queryFn: () => playbookApi.get(playbookId!),
    enabled: !!playbookId && !!leadRef,
  })

  // Step 1b: Match playbook for this lead (only when no playbookId override)
  const { data: matchData, isLoading: matching } = useQuery({
    queryKey: ['playbook-match', leadRef],
    queryFn: () => playbookApi.match(leadRef!),
    enabled: !playbookId && !!leadRef,
  })

  const playbook = (playbookId ? specificData?.data : matchData?.data) as MatchedPlaybook | null
  const isLoadingPlaybook = resumeResponseSetId
    ? (loadingResume || (!!playbookId && loadingSpecific))
    : (playbookId ? loadingSpecific : matching)

  // Step 2: Create draft response set once we have a match (skip when resuming)
  const createDraftMutation = useMutation({
    mutationFn: () =>
      playbookApi.createResponseSet(leadRef!, {
        playbook_id: playbook!.id,
        call_log_id: callLogId || null,
        voip_call_id: voipCallId || null,
      }),
    onSuccess: (data) => {
      const rs = data?.data as any
      setResponseSetId(rs?.id ?? null)
    },
    onError: (err: Error) => setError(err.message),
  })

  useEffect(() => {
    if (resumeResponseSetId) return // Don't create new draft when resuming
    if (leadRef && playbook && !responseSetId && !createDraftMutation.isPending && !createDraftMutation.isSuccess) {
      createDraftMutation.mutate()
    }
  }, [leadRef, playbook, responseSetId, resumeResponseSetId])

  // Gate: if no lead, show lead creation form first
  if (!leadRef) {
    return (
      <ModalShell onClose={onClose} title="CallPulse">
        <CreateLeadGate
          voipCallId={voipCallId}
          callerNumber={callerNumber}
          onCreated={(leadId, leadName) => {
            setResolvedLeadRef(leadId)
            onLeadCreated?.(leadId, leadName)
          }}
          onClose={onClose}
        />
      </ModalShell>
    )
  }

  // Auto-save on blur
  const saveResponsesMutation = useMutation({
    mutationFn: (responsePayload: Record<string, unknown>) =>
      playbookApi.saveResponses(responseSetId!, responsePayload),
  })

  const handleBlur = useCallback(
    (questionId: number) => {
      if (!responseSetId) return
      const value = responses[questionId]
      if (value === undefined && !skipped.has(questionId)) return

      const allResponses = _buildResponsePayload(responses, skipped)
      saveResponsesMutation.mutate({ responses: allResponses })
    },
    [responseSetId, responses, skipped]
  )

  // Submit
  const submitMutation = useMutation({
    mutationFn: () => playbookApi.submitResponseSet(responseSetId!),
    onSuccess: () => {
      setSubmitted(true)
      queryClient.invalidateQueries({ queryKey: ['lead-pulse-responses', leadRef] })
      queryClient.invalidateQueries({ queryKey: ['lead', leadRef] })
      setTimeout(onClose, 1500)
    },
    onError: (err: Error) => setError(err.message),
  })

  const handleSubmit = () => {
    if (!responseSetId) return
    setError('')
    const allResponses = _buildResponsePayload(responses, skipped)
    // If no responses to save, submit directly
    if (allResponses.length === 0) {
      submitMutation.mutate()
      return
    }
    saveResponsesMutation.mutate(
      { responses: allResponses },
      {
        onSuccess: () => submitMutation.mutate(),
        onError: (err: Error) => setError(`Save failed: ${err.message}`),
      }
    )
  }

  const toggleSkip = (questionId: number) => {
    setSkipped((prev) => {
      const next = new Set(prev)
      if (next.has(questionId)) next.delete(questionId)
      else next.add(questionId)
      return next
    })
    // Clear the answer if skipping
    if (!skipped.has(questionId)) {
      setResponses((prev) => {
        const next = { ...prev }
        delete next[questionId]
        return next
      })
    }
  }

  const updateResponse = (questionId: number, value: string | string[]) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }))
    // Remove from skipped if they start typing
    if (skipped.has(questionId)) {
      setSkipped((prev) => {
        const next = new Set(prev)
        next.delete(questionId)
        return next
      })
    }
  }

  // Loading state
  if (isLoadingPlaybook) {
    return (
      <ModalShell onClose={onClose} title="CallPulse">
        <div className="flex items-center justify-center py-12 text-gray-400">
          <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Finding the right questionnaire...
        </div>
      </ModalShell>
    )
  }

  // No matching playbook
  if (!playbook) {
    return (
      <ModalShell onClose={onClose} title="CallPulse">
        <div className="text-center py-8">
          <AlertCircle size={32} className="text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-1">No questionnaire configured for this call</p>
          <p className="text-xs text-gray-400 mb-4">
            Set up a playbook in Settings to see discussion topics here.
          </p>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </ModalShell>
    )
  }

  // Success
  if (submitted) {
    return (
      <ModalShell onClose={onClose} title="CallPulse">
        <div className="text-center py-8">
          <CheckCircle size={40} className="text-green-500 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900">Responses submitted</p>
          <p className="text-xs text-gray-500 mt-1">Discussion notes saved to this lead.</p>
        </div>
      </ModalShell>
    )
  }

  const questions = (playbook.questions || []).sort((a, b) => a.sort_order - b.sort_order)

  return (
    <ModalShell onClose={onClose} title="Discussion Topics">
      <div className="space-y-5">
        {/* Rapport prompt */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800 font-medium">Start with rapport</p>
          <p className="text-xs text-amber-600 mt-0.5">
            Connect personally first -- ask how things are going before diving into questions.
          </p>
        </div>

        {/* Playbook info */}
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <MessageSquare size={12} />
          <span>{playbook.name} (v{playbook.version})</span>
          {playbook.product !== 'all' && (
            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500">{playbook.product}</span>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Questions */}
        {questions.map((q, idx) => (
          <QuestionField
            key={q.id}
            question={q}
            index={idx}
            value={responses[q.id]}
            isSkipped={skipped.has(q.id)}
            onBlur={() => handleBlur(q.id)}
            onChange={(val) => updateResponse(q.id, val)}
            onSkip={() => toggleSkip(q.id)}
          />
        ))}

        {/* Submit */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-400">
            {saveResponsesMutation.isPending ? 'Saving...' : 'Responses auto-save on each field'}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Close
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              loading={submitMutation.isPending}
              disabled={!responseSetId}
            >
              <CheckCircle size={14} /> Submit
            </Button>
          </div>
        </div>
      </div>
    </ModalShell>
  )
}

// ---- Modal Shell ----
function ModalShell({ onClose, title, children }: { onClose: () => void; title: string; children: React.ReactNode }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-12 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="fixed inset-0 bg-black/40" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto p-6">
          {children}
        </div>
      </div>
    </div>
  )
}

// ---- Create Lead Gate (shown when CallPulse is opened without a lead) ----
function CreateLeadGate({
  voipCallId,
  callerNumber,
  onCreated,
  onClose,
}: {
  voipCallId?: number
  callerNumber?: string | null
  onCreated: (leadId: number, leadName: string) => void
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')

  const createMutation = useMutation({
    mutationFn: () => {
      if (!voipCallId) throw new Error('No call ID available')
      return voipApi.createLeadFromCall(voipCallId, {
        name,
        ...(company ? { company } : {}),
        ...(email ? { email } : {}),
      })
    },
    onSuccess: (result) => {
      onCreated(result.lead_id, result.lead_name)
    },
    onError: (err: Error) => setError(err.message),
  })

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-1">
          <UserPlus size={16} className="text-blue-600" />
          <p className="text-sm font-medium text-blue-800">Unknown Caller</p>
        </div>
        <p className="text-xs text-blue-600">
          Create a lead to start CallPulse. The caller's phone number will be attached automatically.
        </p>
      </div>

      {callerNumber && (
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
          <span className="text-xs text-gray-500">Phone</span>
          <span className="text-sm font-mono font-medium text-gray-900">{callerNumber}</span>
        </div>
      )}

      <Input
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Caller's name"
        required
      />
      <Input
        label="Company"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        placeholder="Optional"
      />
      <Input
        label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Optional"
        type="email"
      />

      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}

      <div className="flex items-center gap-2 pt-2">
        <Button variant="secondary" size="sm" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => createMutation.mutate()}
          loading={createMutation.isPending}
          disabled={!name.trim() || !voipCallId}
          className="flex-1"
        >
          <UserPlus size={14} /> Create Lead
        </Button>
      </div>
    </div>
  )
}

// ---- Question Field ----
function QuestionField({
  question,
  index,
  value,
  isSkipped,
  onBlur,
  onChange,
  onSkip,
}: {
  question: PlaybookQuestion
  index: number
  value: string | string[] | undefined
  isSkipped: boolean
  onBlur: () => void
  onChange: (val: string | string[]) => void
  onSkip: () => void
}) {
  const q = question

  return (
    <div className={`rounded-lg border p-4 transition-colors ${isSkipped ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-200'}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            <span className="text-gray-400 mr-1.5">{index + 1}.</span>
            {q.text}
            {q.required && <span className="text-red-400 ml-0.5">*</span>}
          </p>
          {q.helper_text && (
            <p className="text-xs text-gray-400 mt-0.5">{q.helper_text}</p>
          )}
        </div>
        <button
          onClick={onSkip}
          className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors flex-shrink-0 ${
            isSkipped
              ? 'bg-gray-200 text-gray-600'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          }`}
          title={isSkipped ? 'Unskip' : 'Skip this question'}
        >
          <SkipForward size={11} />
          {isSkipped ? 'Skipped' : 'Skip'}
        </button>
      </div>

      {!isSkipped && (
        <div className="mt-2">
          {q.question_type === 'text' && (
            <input
              type="text"
              value={(value as string) || ''}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Type response..."
            />
          )}

          {q.question_type === 'textarea' && (
            <Textarea
              value={(value as string) || ''}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              placeholder="Type response..."
            />
          )}

          {q.question_type === 'select' && (
            <select
              value={(value as string) || ''}
              onChange={(e) => { onChange(e.target.value); setTimeout(onBlur, 100) }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select...</option>
              {(q.options || []).map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          )}

          {q.question_type === 'multiselect' && (
            <div className="flex flex-wrap gap-2">
              {(q.options || []).map((opt) => {
                const selected = Array.isArray(value) ? value.includes(opt) : false
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      const current = Array.isArray(value) ? value : []
                      const next = selected ? current.filter((v) => v !== opt) : [...current, opt]
                      onChange(next)
                      setTimeout(onBlur, 100)
                    }}
                    className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                      selected
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt}
                  </button>
                )
              })}
            </div>
          )}

          {q.question_type === 'yes_no' && (
            <div className="flex gap-2">
              {['Yes', 'No'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => { onChange(opt); setTimeout(onBlur, 100) }}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    value === opt
                      ? opt === 'Yes'
                        ? 'bg-green-100 border-green-300 text-green-700'
                        : 'bg-red-100 border-red-300 text-red-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}

          {q.question_type === 'scale' && (
            <div className="flex gap-1">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => { onChange(String(n)); setTimeout(onBlur, 100) }}
                  className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-colors ${
                    value === String(n)
                      ? 'bg-indigo-500 border-indigo-500 text-white'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {q.ai_tags && q.ai_tags.length > 0 && (
        <div className="flex gap-1 mt-2">
          {q.ai_tags.map((tag) => (
            <span key={tag} className="px-1.5 py-0.5 bg-purple-50 text-purple-600 text-[10px] rounded font-medium">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
